import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Joyride, { Step } from 'react-joyride';
import {
  CalendarDays,
  ClipboardList,
  Wallet,
  XCircle,
  Star,
  CalendarPlus,
  Search,
  ChevronRight,
  Store,
} from 'lucide-react';

import { GLASS_CARD, GLASS_SURFACE } from '@/components/ui/glass';
import { useDashboardCliente, ProximaCita } from '@/components/hooks/useDashboardCliente';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';
import { getTourStyles } from '@/components/tour/tourStyles';
import { getDashboardClientTourSteps } from '@/components/tour/client/DashboardClientTourSteps';
import DashboardHeader from '@/components/pages/company/dashboard/DashboardHeader';
import MetricCard from '@/components/pages/company/dashboard/MetricCard';
import DashboardSeccion from '@/components/pages/company/dashboard/DashboardSeccion';

const BASE = '/panel-cliente';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring' as const, stiffness: 320, damping: 30, mass: 0.7 },
  },
};
const gridVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
};

const shimmer =
  'relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/40 dark:before:via-white/10 before:to-transparent';
const bone = 'rounded-md bg-gray-200/70 dark:bg-gray-700/50';

const MetricsSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className={`relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} p-4 space-y-3`}>
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-xl ${bone} ${shimmer}`} />
          <div className={`h-3 w-20 ${bone} ${shimmer}`} />
        </div>
        <div className={`h-7 w-12 ${bone} ${shimmer}`} />
      </div>
    ))}
  </div>
);

const ListaSkeleton: React.FC = () => (
  <div className={`rounded-2xl ${GLASS_CARD} divide-y divide-gray-100 dark:divide-white/5`}>
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-4">
        <div className={`w-12 h-12 rounded-xl ${bone} ${shimmer}`} />
        <div className="flex-1 space-y-2">
          <div className={`h-3.5 w-40 ${bone} ${shimmer}`} />
          <div className={`h-3 w-24 ${bone} ${shimmer}`} />
        </div>
      </div>
    ))}
  </div>
);

const ESTADOS: Record<string, string> = {
  confirmado: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  pendiente: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  completado: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  cancelado: 'bg-red-500/10 text-red-600 dark:text-red-400',
};

const euro = (v: number) =>
  `${Number(v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const aFecha = (iso: string) => new Date(`${String(iso).slice(0, 10)}T00:00:00`);

const etiquetaDia = (iso: string) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const f = aFecha(iso);
  const dias = Math.round((f.getTime() - hoy.getTime()) / 86400000);
  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Mañana';
  const txt = f.toLocaleDateString('es-ES', { weekday: 'long' });
  return txt.charAt(0).toUpperCase() + txt.slice(1);
};

const FilaCita = ({ cita }: { cita: ProximaCita }) => {
  const f = aFecha(cita.fecha_cita);
  const esHoy = etiquetaDia(cita.fecha_cita) === 'Hoy';
  const estado = cita.estado_cita?.toLowerCase() ?? '';
  return (
    <div className="flex items-center gap-3 p-3.5 sm:p-4">
      <div
        className={`shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center leading-none ${
          esHoy
            ? 'bg-[#2170b8] text-white'
            : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200'
        }`}
      >
        <span className="text-lg font-bold tabular-nums">{f.getDate()}</span>
        <span className={`text-[10px] uppercase ${esHoy ? 'text-white/80' : 'text-gray-400'}`}>
          {f.toLocaleDateString('es-ES', { month: 'short' }).replace('.', '')}
        </span>
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
          {cita.nombre_servicio}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {etiquetaDia(cita.fecha_cita)}
          </span>
          <span className="tabular-nums">
            {cita.hora_cita?.slice(0, 5)}
            {cita.hora_fin ? ` – ${cita.hora_fin.slice(0, 5)}` : ''}
          </span>
          <span className="inline-flex items-center gap-1 min-w-0">
            <Store className="w-3 h-3 shrink-0" />
            <span className="truncate">{cita.nombre_empresa}</span>
          </span>
        </div>
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1">
        <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
          {euro(cita.precio)}
        </span>
        <span
          className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${
            ESTADOS[estado] ?? 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300'
          }`}
        >
          {estado}
        </span>
      </div>
    </div>
  );
};

const DashboardClient: React.FC = () => {
  const navigate = useNavigate();
  const userEmail = localStorage.getItem('email') || '';
  const isMobile = window.innerWidth < 640;

  const { loading, stats, proximasCitas, mes, anio, setMes, setAnio } =
    useDashboardCliente(userEmail);

  const [cargadoUnaVez, setCargadoUnaVez] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const [preparandoTour, setPreparandoTour] = useState(false);
  const [tourSteps, setTourSteps] = useState<Step[]>([]);

  const mostrarSkeleton = loading && !cargadoUnaVez;
  const seccionesAbiertas = preparandoTour || runTour;

  useEffect(() => {
    if (!loading) setCargadoUnaVez(true);
  }, [loading]);

  useEffect(() => {
    if (!preparandoTour) return;
    const id = window.setTimeout(() => {
      setTourSteps(getDashboardClientTourSteps());
      setRunTour(true);
      setPreparandoTour(false);
    }, 350);
    return () => window.clearTimeout(id);
  }, [preparandoTour]);

  usePageHelp(() => setPreparandoTour(true), !mostrarSkeleton);
  useTourVisibility(runTour);

  const metricas = [
    {
      titulo: 'Citas hoy',
      valor: stats.citasHoy,
      icono: <CalendarDays />,
      tono: 'blue' as const,
      onClick: () => navigate(`${BASE}/appointments`),
      linkLabel: 'Ir a mis citas',
      className: 'tour-metric-citasHoy',
    },
    {
      titulo: 'Citas del mes',
      valor: stats.citasMes,
      icono: <ClipboardList />,
      tono: 'blue' as const,
      onClick: () => navigate(`${BASE}/appointments`),
      linkLabel: 'Ir a mis citas',
      className: 'tour-metric-citasMes',
    },
    {
      titulo: 'Gastado en el mes',
      valor: stats.gastoMes,
      icono: <Wallet />,
      tono: 'green' as const,
      isMoney: true,
      etiquetaFija: `${euro(stats.gastoTotal)} en total`,
      className: 'tour-metric-gastoTotal',
    },
    {
      titulo: 'Cancelaciones',
      valor: stats.cancelacionesMes,
      icono: <XCircle />,
      tono: 'red' as const,
      className: 'tour-metric-cancelacionesMes',
    },
  ];

  return (
    <motion.div
      className="space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="tour-title">
        <DashboardHeader mes={mes} anio={anio} setMes={setMes} setAnio={setAnio} />
      </div>

      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        disableOverlayClose={false}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
        }}
        callback={({ status }) => {
          if (status === 'finished' || status === 'skipped') setRunTour(false);
        }}
        styles={getTourStyles(isMobile)}
      />

      {!mostrarSkeleton && (stats.valoracionesPendientes ?? 0) > 0 && (
        <motion.button
          type="button"
          variants={itemVariants}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.995 }}
          onClick={() =>
            navigate(`${BASE}/appointments?estado=completado&valoracion=sin_valorar`)
          }
          className={`tour-valoraciones group w-full text-left relative isolate overflow-hidden flex items-center gap-3 p-4 rounded-2xl ${GLASS_SURFACE} border border-yellow-300/40 dark:border-yellow-500/30 transition-shadow duration-300 hover:shadow-[0_10px_28px_-14px_rgba(202,138,4,0.4)]`}
        >
          <span className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-yellow-500 text-white shadow-[0_4px_12px_-2px_rgba(202,138,4,0.5),inset_0_1px_0_rgba(255,255,255,0.22)]">
            <Star className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Valoraciones pendientes
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tienes <strong>{stats.valoracionesPendientes}</strong>{' '}
              {stats.valoracionesPendientes === 1 ? 'cita' : 'citas'} sin valorar. ¡Tu opinión importa!
            </p>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-amber-500 transition-colors" />
        </motion.button>
      )}

      <DashboardSeccion id="cliente-mes" titulo="Tu mes" forzarAbierta={seccionesAbiertas}>
        {mostrarSkeleton ? (
          <MetricsSkeleton />
        ) : (
          <motion.div
            variants={gridVariants}
            initial="hidden"
            animate="visible"
            className={`grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 transition-opacity duration-200 ${
              loading ? 'opacity-60' : ''
            }`}
          >
            {metricas.map((m) => (
              <MetricCard key={m.className} {...m} />
            ))}
          </motion.div>
        )}
      </DashboardSeccion>

      <DashboardSeccion
        id="cliente-proximas"
        titulo="Próximas citas"
        forzarAbierta={seccionesAbiertas}
        resumen={proximasCitas.length ? `${proximasCitas.length}` : undefined}
      >
        {mostrarSkeleton ? (
          <ListaSkeleton />
        ) : proximasCitas.length === 0 ? (
          <div
            className={`tour-upcoming relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} px-6 py-10 flex flex-col items-center text-center`}
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-[#2170b8]/10 dark:bg-[#2170b8]/15 blur-3xl"
            />
            <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#2170b8] to-[#1e5a8a] text-white shadow-[0_8px_20px_-6px_rgba(30,90,138,0.55),inset_0_1px_0_rgba(255,255,255,0.22)]">
              <CalendarPlus className="w-6 h-6" strokeWidth={2} />
            </span>
            <p className="mt-4 text-base font-semibold text-gray-900 dark:text-white">
              Tu agenda está libre
            </p>
            <p className="mt-1 max-w-xs text-sm text-gray-500 dark:text-gray-400">
              Cuando reserves una cita aparecerá aquí con la hora y el negocio.
            </p>
            <motion.button
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`${BASE}/explore`)}
              className="mt-5 inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-white bg-[#103254] hover:bg-[#0d2742]"
            >
              <Search className="w-4 h-4" />
              Explorar negocios
            </motion.button>
          </div>
        ) : (
          <div className={`tour-upcoming relative isolate overflow-hidden rounded-2xl ${GLASS_CARD}`}>
            <div className="divide-y divide-gray-100 dark:divide-white/5">
              {proximasCitas.map((c) => (
                <FilaCita key={c.id_cita} cita={c} />
              ))}
            </div>
            <div className="grid grid-cols-2 border-t border-gray-100 dark:border-white/5 divide-x divide-gray-100 dark:divide-white/5">
              <button
                type="button"
                onClick={() => navigate(`${BASE}/appointments`)}
                className="tour-upcoming-all flex items-center justify-center gap-1 px-4 py-3 text-xs font-semibold text-[#2170b8] dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                Ver todas mis citas
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => navigate(`${BASE}/explore`)}
                className="tour-book flex items-center justify-center gap-1 px-4 py-3 text-xs font-semibold text-[#2170b8] dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <Search className="w-3.5 h-3.5" />
                Reservar otra cita
              </button>
            </div>
          </div>
        )}
      </DashboardSeccion>
    </motion.div>
  );
};

export default DashboardClient;
